import React, { useEffect, useState } from "react";
import "./style.css";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Steps } from "antd";
import {
  Html5Outlined,
  CodeOutlined,
  NodeIndexOutlined,
  ApiOutlined,
  DatabaseOutlined,
  FireOutlined,
  BorderOutlined,
  BgColorsOutlined,
} from "@ant-design/icons";
import me from "../../assets/images/me.jpg";
import { meta, skills, services } from "../../content_option";
import { Loader } from "lucide-react";
import PageHeader from "components/Header/PageHeader";
import Skills from "components/Home/Skills";
import Services from "components/Home/Services";
const { Step } = Steps;

export const About = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);
  const milestones = [
    {
      year: "2022",
      title: "Started My coding journey with C.",
      description:
        "I practiced problem solving and enhanced my ability to think in a creative manner.",
    },
    {
      year: "2023",
      title: "Started Web development",
      description:
        "Began learning HTML, CSS, and JavaScript and created my first personal website.",
    },
    {
      year: "2024",
      title: "Leveling up",
      description:
        "Developed many e-commerce platform using the MERN stack also a full stack OLX clone, integrating payment gateways and admin panels.",
    },
    {
      year: "2024",
      title: "Hackathons",
      description:
        "Participated in 3 onsite 12 Hours Hackathons at SMIT and succesfully completed the delivarable project.",
    },
    {
      year: "2025",
      title: "Certified MERN stack Web and Mobile app Developer",
      description:
        "Completed a comprehensive full-stack development certification covering advanced React and Node.js topics also including React Native for android development.",
    },
  ];
  if (isLoading) {
    return (
      <div className="loader-container">
        <Loader className="spinner" size={48} />
        <style jsx>{`
          .loader-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            background: #0d1117;
          }
          .spinner {
            animation: spin 1s linear infinite;
            color: #ff0080;
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }
  return (
    <HelmetProvider>
      <div className="about-page-wrapper">
        <Helmet>
          <meta charSet="utf-8" />
          <title>About | {meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>
        <PageHeader
          title="About Me"
          subtitle="Full Stack Developer & Creative Problem Solver"
          breadcrumb={[
            { label: "Home", path: "/" },
            { label: "About", path: "/about" },
          ]}
          height="400px"
        />

        {/* Hero Section with Floating Elements */}
        <section className="hero-about-section pt-0">
          <Container>
            <div className="floating-shapes">
              <div className="shape shape-1"></div>
              <div className="shape shape-2"></div>
              <div className="shape shape-3"></div>
            </div>
            <Row className="hero-content align-items-center min-vh-100">
              <Col lg="6" md="12" className="hero-text-section">
                <div className="hero-text-content">
                  <h1 className="hero-title">
                    <span className="gradient-text">Nouman</span>
                  </h1>
                  <h2 className="hero-subtitle">
                    Full Stack <span className="gradient-text">Developer</span>{" "}
                  </h2>
                  <p className="hero-description">
                    I am a passionate MERN stack developer with a deep love for
                    building scalable, responsive, and user-friendly web
                    applications. With a strong foundation in modern web
                    technologies, I thrive on solving complex problems and
                    delivering high-quality solutions.
                  </p>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-number gradient-text">3+</span>
                      <span className="stat-label">Years Learning</span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <span className="stat-number gradient-text">50+</span>
                      <span className="stat-label">Projects</span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <span className="stat-number gradient-text">3</span>
                      <span className="stat-label">Hackathons</span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg="6" md="12" className="hero-image-section">
                <div className="profile-container">
                  <div className="profile-bg-circle"></div>
                  <div className="profile-image-wrapper">
                    <img src={me} alt="Nouman" className="profile-image" />
                    <div className="profile-glow"></div>
                  </div>
                  <div className="floating-tech-icons">
                    <div className="tech-icon tech-icon-1">
                      <CodeOutlined color="white" />
                    </div>
                    <div className="tech-icon tech-icon-2">
                      <NodeIndexOutlined />
                    </div>
                    <div className="tech-icon tech-icon-3">
                      <DatabaseOutlined />
                    </div>
                    <div className="tech-icon tech-icon-4">
                      <ApiOutlined />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Education & Experience Section */}
        <section className="education-experience-section">
          <Container>
            <Row className="section-header text-center">
              <Col>
                <span className="section-badge">📚 Background</span>
                <h2 className="section-title">Education & Experience</h2>
              </Col>
            </Row>
            <Row className="content-cards">
              <Col lg="6" md="12" className="mb-4">
                <Card className="info-card education-card">
                  <Card.Body>
                    <div className="card-icon">
                      <span className="icon-bg">🎓</span>
                    </div>
                    <h4 className="card-title">Education</h4>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <h5>Bachelor's in Computer Science</h5>
                        <p className="timeline-period">2023 - 2027</p>
                        <p className="timeline-description">
                          Currently pursuing my degree with focus on software
                          engineering, algorithms, and modern web technologies.
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg="6" md="12" className="mb-4">
                <Card className="info-card experience-card">
                  <Card.Body>
                    <div className="card-icon">
                      <span className="icon-bg">💼</span>
                    </div>
                    <h4 className="card-title">Experience</h4>
                    <div className="experience-list">
                      <div className="experience-item">
                        <div className="exp-bullet timeline-description"></div>
                        <p className="timeline-description">
                          2+ years of experience in full-stack web development
                        </p>
                      </div>
                      <div className="experience-item">
                        <div className="exp-bullet"></div>
                        <p className="timeline-description">
                          Expertise in building e-commerce platforms and admin
                          dashboards
                        </p>
                      </div>
                      <div className="experience-item">
                        <div className="exp-bullet timeline-description"></div>
                        <p className="timeline-description">
                          Multiple hackathon participations with successful
                          project delivery
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        <section>
          <Skills />
        </section>
        <section>
          <Services />
        </section>

        {/* Journey Timeline Section */}
        <section className="journey-section">
          <Container>
            <Row className="section-header text-center">
              <Col>
                <span className="section-badge">🚀 Timeline</span>
                <h2 className="section-title">My Journey</h2>
                <p className="section-description">
                  The path that led me to become a full-stack developer
                </p>
              </Col>
            </Row>
            <Row className="journey-timeline">
              <Col>
                <div className="timeline-wrapper">
                  <Steps
                    direction="horizontal"
                    progressDot
                    current={milestones.length - 1}
                    className="premium-timeline"
                  >
                    {milestones.map((milestone, index) => (
                      <Step
                        key={index}
                        title={
                          <div className="timeline-year">
                            <span className="year-badge">{milestone.year}</span>
                          </div>
                        }
                        description={
                          <div className="timeline-content">
                            <h5 className="timeline-title">
                              {milestone.title}
                            </h5>
                            <p className="timeline-desc">
                              {milestone.description}
                            </p>
                          </div>
                        }
                      />
                    ))}
                  </Steps>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </HelmetProvider>
  );
};
